/**
 * The same concept under several raw names (ORDER_NO · ItemNumber · sku · ArticleIdentifier) is one attribute of
 * what was found, not four (ui/DESIGN.md, "Show, don't label": raw field names never reach the lens). Facets are
 * grouped by the family the cloud knows, else by the concept their name says, else by the values they share; a
 * merged facet keeps every path (so the trail narrows down in all of them) and names the raw ones as its sources.
 * Pure, so `node --test src/stage/lenses/facets.test.ts` runs it as is.
 */
import type { Facet } from '../../lib/api.ts';
import { humanize } from '../../lib/format.ts';

/** A facet made of several, with the facets it came from. */
export interface MergedFacet extends Facet { sources: Facet[] }

/** Concepts raw field names spell in many ways; checked on the name with only its letters left. */
const CONCEPTS: [string, RegExp][] = [
  ['item number', /^(item|article|product|sku)(no|nr|num|number|id|identifier|code)?$/],
  ['order number', /^(sales)?order(no|nr|num|number|id|identifier|reference|ref)?$|^salesid$/],
  ['delivery method', /^(delivery|shipping|shipment)(mode|method|type)$/],
  ['item name', /^(item|article|product)(title|name|description)$|^title$/],
  ['quantity', /^(qty|quantity|amount|pieces|pcs)$/],
  ['status', /^(status|state|salesstatus|orderstatus|invoicestatus)$/],
];

const letters = (s: string) => s.replace(/^@/, '').replace(/__c$/i, '').toLowerCase().replace(/[^a-z]/g, '');

/** The concept a facet is about: its family, else what its name says, else nothing. */
export function conceptOf(f: Pick<Facet, 'label' | 'family'>): string | null {
  if (f.family) return `family:${f.family}`;
  const key = letters(f.label);
  for (const [concept, re] of CONCEPTS) if (re.test(key)) return concept;
  return null;
}

/** The share of the smaller facet's values the other has too (case does not matter). */
function overlap(a: Facet, b: Facet): number {
  const va = new Set(a.values.map((v) => v.value.toLowerCase()));
  const vb = new Set(b.values.map((v) => v.value.toLowerCase()));
  if (va.size === 0 || vb.size === 0) return 0;
  let n = 0;
  for (const v of va) if (vb.has(v)) n++;
  return n / Math.min(va.size, vb.size);
}

/** The best name among raw ones: one already written for people (with spaces, not all caps), else the first. */
function bestLabel(fs: Facet[]): string {
  const score = (l: string) => (/\s/.test(l) ? 3 : /\s/.test(humanize(l)) ? 1 : 0) + (l === l.toUpperCase() ? -1 : 0) + (l.length > 3 ? 1 : 0);
  return [...fs].sort((a, b) => score(b.label) - score(a.label))[0].label;
}

/** Facets that are one attribute, merged: every path, the values summed by value, the systems together. */
function merge(fs: Facet[], label: string): MergedFacet {
  if (fs.length === 1) return { ...fs[0], label, sources: fs };
  const values = new Map<string, { value: string; count: number }>();
  for (const f of fs) {
    for (const v of f.values) {
      const k = v.value.toLowerCase();
      const cur = values.get(k);
      if (cur) cur.count += v.count;
      else values.set(k, { value: v.value, count: v.count });
    }
  }
  const paths = fs.flatMap((f) => f.paths).filter((p, i, all) => all.findIndex((x) => x.cls === p.cls && x.path === p.path) === i);
  return {
    id: fs.map((f) => f.id).join('+'),
    label,
    family: fs.find((f) => f.family)?.family ?? null,
    paths,
    values: [...values.values()].sort((a, b) => b.count - a.count),
    distinct: Math.max(values.size, ...fs.map((f) => f.distinct)),
    more: fs.some((f) => f.more),
    systems: [...new Set(fs.flatMap((f) => f.systems))],
    sources: fs,
  };
}

/** Values two facets must share to be the same concept: most of the smaller one's, and at least three. */
const SAME = 0.6;
const MIN_SHARED = 3;

/** The facets as one attribute per concept, in the order the first of each came. */
export function mergeFacets(facets: Facet[]): MergedFacet[] {
  const groups: { key: string; concept: string | null; fs: Facet[] }[] = [];
  for (const f of facets) {
    const concept = conceptOf(f);
    const key = concept ?? `label:${letters(f.label)}`;
    const g = groups.find((x) => x.key === key);
    if (g) g.fs.push(f);
    else groups.push({ key, concept, fs: [f] });
  }
  // Two names the cloud does not know as one concept, but whose values are the same values, are one attribute too
  for (let i = 0; i < groups.length; i++) {
    for (let j = groups.length - 1; j > i; j--) {
      const a = groups[i];
      const b = groups[j];
      if (a.concept?.startsWith('family:') || b.concept?.startsWith('family:')) continue;
      const shared = Math.min(a.fs[0].values.length, b.fs[0].values.length) >= MIN_SHARED && a.fs.some((x) => b.fs.some((y) => overlap(x, y) >= SAME));
      if (!shared) continue;
      a.fs.push(...b.fs);
      a.concept ??= b.concept;
      groups.splice(j, 1);
    }
  }
  const cap = (l: string) => l.charAt(0).toUpperCase() + l.slice(1);
  return groups.map((g) => {
    const label = g.concept && !g.concept.startsWith('family:') ? cap(g.concept) : g.fs.length > 1 ? humanize(bestLabel(g.fs)) : g.fs[0].label;
    return merge(g.fs, label);
  });
}
